import { useState, useContext, useEffect, Dispatch, SetStateAction } from "react";
import styles from "./CommonFields.module.css";
import {
    Checkbox,
    FormLayout,
    TextField,
    Icon,
    Card,
    Tooltip,
    Select,
    RadioButton,
    BlockStack,
    Text,
} from "@shopify/polaris";
import { Field } from "@shopify/react-form";
import { DISCOUNT_TYPES, generateUniqueId, getDiscountOptions, validateBBTierDiscounts } from "../constants";
import { AppConfigContext } from "../../../context/AppConfigContext";
import { BBTierOptions, DiscountErrorInterface } from "../Types";
import { AlertTriangleIcon, DeleteIcon, PlusIcon } from "@shopify/polaris-icons";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";
import { getNumberFieldSuffix } from "../../../utilities/constants";
import { PiBracketsCurlyBold } from "react-icons/pi";
import RenderSelectedProduct2 from "../../../components/RenderSelectedProduct2/RenderSelectedProduct2";
import { buildResourcePickerQuery, extractNumericIds } from "../../constants";
import useIsMobile from "../../../hooks/useIsMobile";

const TierCard = ({
    option,
    index,
    handleDeleteOption,
    handleDiscountInputChange,
    errors,
    money_format,
    tier_discount_basis,
    discount_options,
    bundleType,
}: {
    option: BBTierOptions;
    index: number;
    handleDeleteOption: any;
    handleDiscountInputChange: any;
    errors: any;
    money_format: any;
    setErrors: Dispatch<SetStateAction<DiscountErrorInterface[]>>;
    tier_discount_basis: any;
    discount_options: any;
    bundleType: string;
}) => {
    useEffect(() => {
        if (option.discountType === DISCOUNT_TYPES.FIXED && option.addRange) {
            // "minimum" was selected , we switched to "fixed" as it is fixed budle level discount
            handleDiscountInputChange(index, "addRange", false); // move to "exact"
        }
    }, [option.discountType]);
    return (
        <Card background="bg-surface-secondary">
            <div key={option.id} className={styles.optionContainer}>
                {/* Header with Option label and Delete icon */}
                <div className={styles.optionHeader}>
                    <label className={styles.optionLabel}>OPTION {index + 1}</label>
                    <span className={styles.deleteIcon} onClick={() => handleDeleteOption(index)}>
                        <Icon source={DeleteIcon} tone="base" />
                    </span>
                </div>

                {tier_discount_basis.value === "product_based" && (
                    <div style={{ marginTop: "10px" }}>
                        <RenderSelectedProduct2
                            bundleType={bundleType}
                            products={option.selected_products || []}
                            submit={() => {}}
                            isMobile={false}
                            resourcePickerQuery={buildResourcePickerQuery({
                                products: extractNumericIds(
                                    discount_options
                                        ?.filter((_: any, i: number) => i !== index)
                                        .flatMap((opt: any) => opt.selected_products || []),
                                ),
                                filters: ["exclude"],
                            })}
                            handleFieldChange={(products: any) => {
                                handleDiscountInputChange(index, "selected_products", products);
                            }}
                            showLabel={true}
                        />
                    </div>
                )}

                <div
                    className={tier_discount_basis.value === "product_based" ? styles.formRow : ""}
                    style={{ ...(tier_discount_basis.value === "product_based" ? { alignItems: "center" } : {}) }}
                >
                    <div
                        style={{
                            margin: "10px 0",
                        }}
                    >
                        <Select
                            label={"Discount Type"}
                            options={getDiscountOptions(money_format.split("{{")[0].trim()).filter((op) => {
                                if (tier_discount_basis.value === "product_based") {
                                    return op.value === DISCOUNT_TYPES.PERCENTAGE || op.value === DISCOUNT_TYPES.FLAT;
                                }
                                return op.value != "none";
                            })}
                            onChange={(val) => handleDiscountInputChange(index, "discountType", val)}
                            value={option.discountType}
                        />
                    </div>

                    {/* Quantity and Max Quantity in one line */}
                    <div
                        className={
                            tier_discount_basis.value !== "product_based"
                                ? `${styles.formRow} ${option.discountType === DISCOUNT_TYPES.FIXED ? "" : styles.row4}`
                                : ""
                        }
                    >
                        {option.discountType !== DISCOUNT_TYPES.FIXED &&
                            tier_discount_basis.value !== "product_based" && (
                                <Select
                                    label={"Tier Discounts On"}
                                    options={[
                                        {
                                            value: "minimum",
                                            label: `Minimum ${
                                                tier_discount_basis.value == "bundle_total" ? "amount" : "products"
                                            } for this tier`,
                                        },
                                        {
                                            value: "exact",
                                            label: `Exact ${
                                                tier_discount_basis.value == "bundle_total" ? "amount" : "products"
                                            } for this tier`,
                                        },
                                    ]}
                                    onChange={(val) => {
                                        handleDiscountInputChange(index, "addRange", val === "minimum");
                                    }}
                                    value={option?.addRange ? "minimum" : "exact"}
                                />
                            )}

                        {tier_discount_basis.value !== "product_based" && (
                            <TextField
                                label={`${tier_discount_basis?.value == "items_count" ? "Quantity" : "Total"}`}
                                autoComplete="off"
                                type={"number"}
                                placeholder="0"
                                error={errors[index]?.quantity}
                                suffix={
                                    tier_discount_basis?.value == "bundle_total"
                                        ? getNumberFieldSuffix("fixed", money_format).suffix
                                        : ""
                                }
                                value={option.quantity?.toString()}
                                onBlur={(e: any) => {
                                    handleDiscountInputChange(
                                        index,
                                        "quantity",
                                        Number(e?.target.value) <= 0 ? 1 : Number(e?.target.value),
                                    );
                                }}
                                onChange={(val) => handleDiscountInputChange(index, "quantity", +val)}
                            />
                        )}

                        <TextField
                            label={
                                option?.discountType === DISCOUNT_TYPES.PERCENTAGE
                                    ? "% Discount Off"
                                    : option?.discountType === DISCOUNT_TYPES.FLAT
                                      ? "Fixed Amount Off"
                                      : option?.discountType === DISCOUNT_TYPES.FIXED
                                        ? "Fixed Price"
                                        : "Discount Value"
                            }
                            autoComplete="off"
                            type={"number"}
                            placeholder={"Enter value"}
                            error={errors[index]?.discountValue}
                            suffix={getNumberFieldSuffix(option.discountType, money_format).suffix}
                            // min={1}
                            value={option.discountValue?.toString()}
                            onBlur={(e: any) => {
                                let val = e?.target.value || 0;
                                const regex = /^(\d+(\.\d{0,2})?)?$/;
                                if (regex.test(val)) {
                                    if (option.discountType === "percentage" && +val > 100) {
                                        val = "100";
                                    }
                                    handleDiscountInputChange(index, "discountValue", +val || 0);
                                }
                            }}
                            onChange={(val) => {
                                const regex = /^(\d+(\.\d{0,2})?)?$/;
                                if (regex.test(val)) {
                                    if (option.discountType === "percentage" && +val > 100) {
                                        val = "100";
                                    }

                                    handleDiscountInputChange(index, "discountValue", +val);
                                }
                            }}
                        />
                    </div>
                </div>

                {tier_discount_basis.value !== "product_based" && (
                    <>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 2,
                                width: "100%",
                                marginTop: 10,
                            }}
                        >
                            <Checkbox
                                label={"Message Before Tier Completion"}
                                checked={option.ShowTierSuccessMessage}
                                value={""}
                                onChange={(val) => handleDiscountInputChange(index, "ShowTierSuccessMessage", val)}
                            />
                            <Tooltip
                                width="wide"
                                content={
                                    <>
                                        {tier_discount_basis?.value == "items_count" && (
                                            <div>
                                                {`{noOfRemainingProducts} – Number of additional products required to unlock the
                                        discount.`}
                                            </div>
                                        )}

                                        {tier_discount_basis?.value == "bundle_total" && (
                                            <div>{`{remainingAmount} - Amount remaining to unlock the discount.`}</div>
                                        )}
                                        <div>
                                            {`{discount} – The discount amount and type you’ll receive (e.g., 10% off, ₹100 off).`}
                                        </div>
                                    </>
                                }
                            >
                                <PiBracketsCurlyBold />
                            </Tooltip>
                        </div>
                        <TextField
                            label={""}
                            disabled={!option.ShowTierSuccessMessage}
                            placeholder={"Add {noOfRemainingProducts} more product to get {discount} discount!"}
                            helpText={<></>}
                            autoComplete={"off"}
                            value={option?.TierSuccessMessage}
                            onChange={(val) => handleDiscountInputChange(index, "TierSuccessMessage", val)}
                            maxLength={300}
                            // showCharacterCount
                        />
                    </>
                )}
            </div>
        </Card>
    );
};

const BBTierDiscounts = ({
    discount_options,
    tier_discount_basis,
    dispatchBundleConfig,
    products,
    show_progress_bar,
    bundleType,
}: {
    discount_options: Field<BBTierOptions[]>;
    dispatchBundleConfig: any;
    tier_discount_basis: Field<"items_count" | "bundle_total" | "product_based">;
    products: any;
    show_progress_bar: any;
    bundleType: string;
}) => {
    const appConfigContext = useContext(AppConfigContext);
    const [errors, setErrors] = useState<DiscountErrorInterface[]>([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const { money_format } = appConfigContext;
    const { trackCtaClick } = useBundleMixpanelHook();
    const { isMobile } = useIsMobile();

    const handleTierBasisChange = (val: "bundle_total" | "items_count" | "product_based") => {
        tier_discount_basis?.onChange(val);
        dispatchBundleConfig({
            payload: val,
            key: "tier_discount_basis",
        });
        let discounts = [...discount_options.value];
        discounts = discounts?.map((opt) => {
            let updatedMsg = "";

            if (opt.discountType === DISCOUNT_TYPES.FIXED) {
                updatedMsg =
                    val != "bundle_total"
                        ? "Add {noOfRemainingProducts} more product(s) to unlock the fixed price offer of {discount}!"
                        : "Add products worth {remainingAmount} more to unlock the fixed price offer of {discount}!";
            } else {
                updatedMsg =
                    val != "bundle_total"
                        ? "Add {noOfRemainingProducts} more product(s) to get {discount} discount!"
                        : "Add products worth {remainingAmount} more to get {discount} discount!";
            }

            if (val === "product_based") {
                updatedMsg = "";
            }

            if (opt.TierSuccessMessage !== updatedMsg) {
                const updatedOpt: BBTierOptions = { ...opt, TierSuccessMessage: updatedMsg };
                if (val === "product_based") {
                    if (opt.discountType === DISCOUNT_TYPES.FIXED) {
                        updatedOpt.discountType = DISCOUNT_TYPES.PERCENTAGE;
                    }
                }
                return updatedOpt;
            } else return opt;
        });

        handleUpdateDiscountOptions([...discounts]);
    };

    const handleDiscountInputChange = (
        index: number,
        field: keyof BBTierOptions,
        value: string | number | boolean | null,
    ) => {
        console._log("BBTD Index:", index);
        console._log("BBTD field:", field);
        console._log("BBTD Value:", value);
        const updatedDiscountOptions = [...discount_options.value];
        if (field && index >= 0 && updatedDiscountOptions[index] && value != null) {
            // Create a new object to avoid direct state mutation
            const updatedItem = { ...updatedDiscountOptions[index], [field]: value };
            // Handle the specific case where quantity is updated
            if (field === "quantity" && typeof value === "number") {
                updatedItem.maxQuantity = value;
            }
            if (field == "showFinalSuccessMessage" && value && !updatedItem?.finalSuccessMessage?.length) {
                updatedItem.finalSuccessMessage = "Congratulations! You've got the best offer on your bundle.";
            }
            if (field == "discountType") {
                if (value === DISCOUNT_TYPES.FIXED) {
                    updatedItem.TierSuccessMessage =
                        tier_discount_basis.value != "bundle_total"
                            ? "Add {noOfRemainingProducts} more product(s) to unlock the fixed price offer of {discount}!"
                            : "Add products worth {remainingAmount} more to unlock the fixed price offer of {discount}!";
                } else if (tier_discount_basis.value == "product_based") {
                    updatedItem.TierSuccessMessage = "";
                } else {
                    updatedItem.TierSuccessMessage =
                        tier_discount_basis.value != "bundle_total"
                            ? "Add {noOfRemainingProducts} more product(s) to get {discount} discount!"
                            : "Add products worth {remainingAmount} more to get {discount} discount!";
                }
            }
            updatedDiscountOptions[index] = updatedItem;
        }
        validateBBTierDiscounts(updatedDiscountOptions, setErrors, tier_discount_basis.value);
        handleUpdateDiscountOptions(updatedDiscountOptions);
    };

    const handleAddOption = () => {
        if (discount_options?.value?.length < 6) {
            const updatedDiscountOptions: BBTierOptions[] = [...discount_options.value];
            const lastOption = updatedDiscountOptions[updatedDiscountOptions.length - 1];
            console._log("lastOption", lastOption);
            let successMessage = "";
            if (lastOption.discountType === DISCOUNT_TYPES.FIXED) {
                successMessage =
                    tier_discount_basis.value != "bundle_total"
                        ? "Add {noOfRemainingProducts} more product(s) to unlock the fixed price offer of {discount}!"
                        : "Add products worth {remainingAmount} more to unlock the fixed price offer of {discount}!";
            } else if (tier_discount_basis.value == "product_based") {
                successMessage = "";
            } else {
                successMessage =
                    tier_discount_basis.value != "bundle_total"
                        ? "Add {noOfRemainingProducts} more product(s) to get {discount} discount!"
                        : "Add products worth {remainingAmount} more to get {discount} discount!";
            }
            // Add the new option
            updatedDiscountOptions.push({
                id: generateUniqueId(),
                discountValue:
                    lastOption.discountType == "percentage" && +lastOption.discountValue == 100
                        ? 100
                        : +lastOption?.discountValue + 5,
                quantity: +lastOption.quantity + 1,
                discountType: lastOption.discountType,
                maxQuantity: +lastOption.quantity + 1,
                addRange: true,
                ShowTierSuccessMessage: tier_discount_basis.value !== "product_based",
                TierSuccessMessage: successMessage,
                selected_products: [],
            });

            trackCtaClick({
                cta: "Discount option CTA",
                ctaType: "discount option added",
                bundleType: bundleType,
            });

            // Update discount_options state and dispatch to bundle config
            validateBBTierDiscounts(updatedDiscountOptions, setErrors, tier_discount_basis.value);
            handleUpdateDiscountOptions(updatedDiscountOptions);

            setSelectedTab(discount_options.value.length);
        }
    };

    const handleDeleteOption = (index: number) => {
        if (discount_options?.value?.length === 1) {
            shopify.toast.show("At least one option is necessary", {
                isError: true,
            });
            return;
        }

        trackCtaClick({
            cta: "Discount option CTA",
            ctaType: "discount option deleted",
            bundleType: bundleType,
        });
        const updatedOptions = discount_options.value.filter((_: any, i: number) => i !== index);
        validateBBTierDiscounts(updatedOptions, setErrors, tier_discount_basis.value);
        handleUpdateDiscountOptions(updatedOptions);
        setSelectedTab(0);
    };
    const workOnBlur = () => {
        for (let i = 0; i < discount_options.value.length; i++) {
            const element = discount_options.value[i];

            if (element.discountType == "percentage" && element.discountValue > 100) {
                handleDiscountInputChange(i, "discountValue", 100);
            }
            if (typeof element.discountValue == "string") {
                handleDiscountInputChange(i, "discountValue", Number(element.discountValue) || 0);
            }
            if (typeof element.quantity == "string") {
                handleDiscountInputChange(i, "quantity", Number(element.quantity) || 1);
            }
            if (typeof element.maxQuantity == "string") {
                handleDiscountInputChange(i, "maxQuantity", Number(element.maxQuantity) || 1);
            }
            if (element.discountType == "percentage" && element.discountValue > 100) {
                handleDiscountInputChange(i, "discountValue", 100);
            }

            if (element.discountValue < 0) {
                handleDiscountInputChange(i, "discountValue", 1);
            }

            if (element.discountType === "none") {
                handleDiscountInputChange(i, "discountValue", 1);
            }
        }
    };

    const handleDragEnd = (result: any) => {
        if (!result.destination) return;

        const reorderedOffers: BBTierOptions[] = Array.from(discount_options.value);
        const [movedOffer] = reorderedOffers.splice(result.source.index, 1);

        reorderedOffers.splice(result.destination.index, 0, movedOffer);
        const reorderedErrors = Array.from(errors);
        const [movedError] = reorderedErrors.splice(result.source.index, 1);
        reorderedErrors.splice(result.destination.index, 0, movedError);
        setErrors(reorderedErrors);
        validateBBTierDiscounts(reorderedOffers, setErrors, tier_discount_basis.value);
        handleUpdateDiscountOptions(reorderedOffers);
        setSelectedTab(result.destination.index);
    };

    const handleUpdateDiscountOptions = (updated: BBTierOptions[]) => {
        discount_options.onChange(updated);
        dispatchBundleConfig({
            payload: updated,
            key: "discount_options",
        });
    };

    useEffect(() => {
        workOnBlur();
    }, [products.value]);

    return (
        <>
            {/* <div className={styles.sectionTitleWrapper}>
                <div>Discount options</div>
            </div> */}
            <FormLayout>
                <div className={styles.container}>
                    <BlockStack gap="200">
                        <Text as="h3" variant="headingSm" fontWeight="bold">
                            2. Discount Logic
                        </Text>
                        <Text as="p" variant="bodyMd">
                            Apply these levels based on:
                        </Text>
                        <div
                            style={{
                                display: "flex",
                                gap: isMobile ? "5px" : "20px",
                                flexDirection: isMobile ? "column" : "row",
                            }}
                        >
                            <RadioButton
                                label="Number of Items added"
                                checked={tier_discount_basis?.value === "items_count"}
                                id="items_count"
                                name="tier_discount_basis"
                                onChange={() => handleTierBasisChange("items_count")}
                            />
                            <RadioButton
                                label="Total Cart Amount"
                                checked={tier_discount_basis?.value === "bundle_total"}
                                id="bundle_total"
                                name="tier_discount_basis"
                                onChange={() => handleTierBasisChange("bundle_total")}
                            />
                            <RadioButton
                                label="Product Based"
                                checked={tier_discount_basis?.value === "product_based"}
                                id="product_based"
                                name="tier_discount_basis"
                                onChange={() => handleTierBasisChange("product_based")}
                            />
                        </div>
                    </BlockStack>

                    <div className={styles.offerTabContainer}>
                        {
                            // @ts-expect-error required
                            <DragDropContext onDragEnd={handleDragEnd}>
                                {
                                    // @ts-expect-error required
                                    <Droppable droppableId="droppable-tabs" direction="horizontal">
                                        {(provided) => (
                                            <div
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                className={styles.offerTabDroppableContainer}
                                                style={{
                                                    borderBottom: "1px solid #E1E3E5",
                                                }}
                                            >
                                                {discount_options?.value?.map((_, index) => (
                                                    // @ts-expect-error required
                                                    <Draggable key={index} draggableId={index.toString()} index={index}>
                                                        {(provided, snapshot) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                onClick={() => setSelectedTab(index)}
                                                                className={`${styles.offerTabDraggableItem} ${
                                                                    snapshot.isDragging
                                                                        ? styles.offerTabDraggableItemDragging
                                                                        : ""
                                                                }`}
                                                                style={{ padding: "0px" }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        padding: "8px 16px",
                                                                        backgroundColor:
                                                                            selectedTab === index
                                                                                ? "#f4f6f8"
                                                                                : "transparent",
                                                                        color:
                                                                            selectedTab === index
                                                                                ? "#202223"
                                                                                : "#6D7175",
                                                                        cursor: "pointer",
                                                                        whiteSpace: "nowrap",
                                                                        fontWeight:
                                                                            selectedTab === index ? "bold" : "normal",
                                                                        fontSize: "14px",
                                                                        border: "1px solid transparent",
                                                                        borderBottom:
                                                                            selectedTab === index
                                                                                ? "none"
                                                                                : "1px solid transparent",
                                                                        borderRadius: "8px 8px 0 0",
                                                                        ...(selectedTab === index && {
                                                                            border: "1px solid #E1E3E5",
                                                                            borderBottom: "1px solid white",
                                                                            marginBottom: "-1px",
                                                                        }),
                                                                    }}
                                                                >
                                                                    {tier_discount_basis.value === "product_based"
                                                                        ? `Group ${index + 1}`
                                                                        : `Tier ${index + 1}`}{" "}
                                                                    {errors[index]?.quantity && (
                                                                        <Icon
                                                                            source={AlertTriangleIcon}
                                                                            tone={"warning"}
                                                                        />
                                                                    )}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder as any}
                                                {discount_options.value.length < 6 && (
                                                    <button
                                                        style={{
                                                            padding: "8px 16px",
                                                            backgroundColor: "transparent",
                                                            border: "none",
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "6px",
                                                            color: "#005BD3",
                                                            fontSize: "14px",
                                                            fontWeight: "500",
                                                        }}
                                                        onClick={handleAddOption}
                                                    >
                                                        <Icon source={PlusIcon} tone="interactive" />
                                                        {tier_discount_basis.value === "product_based"
                                                            ? "Add Group"
                                                            : "Add Tier"}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </Droppable>
                                }
                            </DragDropContext>
                        }
                    </div>
                    {/* Display the content of the selected tab */}
                    {discount_options?.value[selectedTab] && (
                        <TierCard
                            key={discount_options.value[selectedTab]?.id}
                            option={discount_options.value[selectedTab]}
                            index={selectedTab}
                            handleDeleteOption={handleDeleteOption}
                            errors={errors}
                            money_format={money_format}
                            handleDiscountInputChange={handleDiscountInputChange}
                            setErrors={setErrors}
                            tier_discount_basis={tier_discount_basis}
                            discount_options={discount_options.value}
                            bundleType={bundleType}
                        />
                    )}

                    {tier_discount_basis.value !== "product_based" && (
                        <BlockStack gap="200">
                            <Text as="h3" variant="headingSm" fontWeight="bold">
                                3. Final Completion Message
                            </Text>

                            <Card padding="400" background="bg-surface-secondary">
                                <div style={{ display: "flex", flexDirection: "column" }}>
                                    <Checkbox
                                        label="Show Progress Bar"
                                        checked={show_progress_bar.value}
                                        value={""}
                                        onChange={(checked: boolean) => {
                                            dispatchBundleConfig({
                                                key: "show_progress_bar",
                                                payload: checked,
                                            });
                                            show_progress_bar.onChange(checked);
                                        }}
                                    />

                                    <Checkbox
                                        label="Success Message After All Tier Completion"
                                        checked={discount_options.value?.[0]?.showFinalSuccessMessage}
                                        value={""}
                                        onChange={(val) => handleDiscountInputChange(0, "showFinalSuccessMessage", val)}
                                    />
                                </div>

                                <TextField
                                    label={""}
                                    disabled={!discount_options.value?.[0]?.showFinalSuccessMessage}
                                    placeholder={"Congratulations! You've got the best offer on your bundle."}
                                    autoComplete={"off"}
                                    value={discount_options.value?.[0]?.finalSuccessMessage}
                                    onChange={(val) => handleDiscountInputChange(0, "finalSuccessMessage", val)}
                                    maxLength={300}
                                    // showCharacterCount
                                />
                            </Card>
                        </BlockStack>
                    )}
                </div>
            </FormLayout>
        </>
    );
};

export default BBTierDiscounts;
